import type { GraphQLError, GraphQLFormattedError } from 'graphql';

import type { AuthErrorCode } from './sessionClient';

const CODE_REAUTHENTICATION_REQUIRED = 'reauthentication-required';

/** Fallback copy for auth submissions that fail without a user-facing message. */
export const GENERIC_AUTH_FAILURE_MESSAGE = 'Something went wrong. Please try again.';

// Expected credential outcomes of a login attempt — the user mistyped or the
// account is (about to be) locked. These are surfaced in-form and are never
// worth an error reporter's attention, unlike network/5xx/unknown failures.
const EXPECTED_LOGIN_FAILURE_CODES: ReadonlySet<AuthErrorCode> = new Set([
  'invalid-credentials',
  'lockout-imminent',
  'locked',
]);

export const isExpectedLoginFailure = (code: AuthErrorCode): boolean =>
  EXPECTED_LOGIN_FAILURE_CODES.has(code);

export const isFetchResponseReauthRequired = async (resp: Response): Promise<boolean> => {
  if (resp.status !== 403) {
    return false;
  }

  try {
    const json = await resp.json();
    return json?.metadata?.code === CODE_REAUTHENTICATION_REQUIRED || false;
  } catch {
    return false;
  }
};

type GraphQLAuthErrorCode = 'UNAUTHENTICATED' | 'FORBIDDEN' | 'REAUTHENTICATION_REQUIRED';

type GraphQLAuthError = Pick<GraphQLFormattedError, 'message' | 'extensions'> & {
  code?: string;
};

const IMPERSONATION_DENIED_MESSAGE = 'This action is not available while impersonating a customer.';
const PERMISSION_DENIED_MESSAGE = 'You do not have permission to perform this action.';
const ATHENA_NO_PROFILE_MESSAGE = 'user profile is not available on context/session';

export function getGraphQLAuthErrorCode(error: GraphQLAuthError): GraphQLAuthErrorCode | undefined {
  const code: unknown = error.extensions?.['code'] ?? error.code;
  if (code === 'FORBIDDEN' && error.message.startsWith(ATHENA_NO_PROFILE_MESSAGE)) {
    return 'UNAUTHENTICATED';
  }
  switch (code) {
    case 'UNAUTHENTICATED':
    case 'FORBIDDEN':
    case 'REAUTHENTICATION_REQUIRED':
      return code;
    case 'UNAUTHORIZED':
      return 'FORBIDDEN';
  }
  return undefined;
}

export function getGraphQLAuthErrorMessage(error: GraphQLAuthError): string | undefined {
  switch (getGraphQLAuthErrorCode(error)) {
    case 'UNAUTHENTICATED':
      return 'Please sign in to continue.';
    case 'REAUTHENTICATION_REQUIRED':
      return 'Please sign in again to continue.';
    case 'FORBIDDEN':
      return error.message === IMPERSONATION_DENIED_MESSAGE
        ? IMPERSONATION_DENIED_MESSAGE
        : PERMISSION_DENIED_MESSAGE;
  }
  return undefined;
}

export const isGraphQLErrorUnauthenticated = (
  error: GraphQLError | GraphQLFormattedError,
): boolean =>
  getGraphQLAuthErrorCode(error) === 'UNAUTHENTICATED' ||
  error.message.startsWith('HTTP Error 401');

export const isGraphQLErrorUnauthorized = (
  error: GraphQLError | GraphQLFormattedError,
): boolean => {
  const code = getGraphQLAuthErrorCode(error);
  return (
    code === 'FORBIDDEN' ||
    code === 'REAUTHENTICATION_REQUIRED' ||
    error.message.startsWith('HTTP Error 403')
  );
};

export const isGraphQLErrorReauthRequired = (
  error: GraphQLError | GraphQLFormattedError,
): boolean =>
  getGraphQLAuthErrorCode(error) === 'REAUTHENTICATION_REQUIRED' ||
  (error.message.startsWith('HTTP Error 403') &&
    error.message.includes(`"code":"${CODE_REAUTHENTICATION_REQUIRED}"`));
